import React from "react";
import {
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  Box,
  Avatar,
  Menu,
  MenuItem,
  Tooltip,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { Menu as MenuIcon } from "@mui/icons-material";
import { useAuthenticatedUserState } from "../../providers/AuthenticatedUserProvider";
import { DRAWER_WIDTH, DRAWER_COLLAPSED_WIDTH } from "./Sidebar";

interface TopBarProps {
  onMenuClick: () => void;
  title: string;
  sidebarOpen: boolean;
}

export const TopBar: React.FC<TopBarProps> = ({
  onMenuClick,
  title,
  sidebarOpen,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const { signOut } = useAuthenticatedUserState();

  const [anchorElUser, setAnchorElUser] = React.useState<null | HTMLElement>(
    null
  );

  const handleOpenUserMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElUser(event.currentTarget);
  };

  const handleCloseUserMenu = () => {
    setAnchorElUser(null);
  };

  // Calculate margin-left based on sidebar state (only on desktop)
  const marginLeft = isMobile
    ? 0
    : sidebarOpen
    ? DRAWER_WIDTH
    : DRAWER_COLLAPSED_WIDTH;

  return (
    <AppBar
      position="fixed"
      elevation={1}
      sx={{
        backgroundColor: "white",
        color: "text.primary",
        zIndex: theme.zIndex.drawer + 1,
        width: isMobile ? "100%" : `calc(100% - ${marginLeft}px)`,
        ml: isMobile ? 0 : `${marginLeft}px`,
        transition: theme.transitions.create(["width", "margin"], {
          easing: theme.transitions.easing.sharp,
          duration: theme.transitions.duration.enteringScreen,
        }),
      }}
    >
      <Toolbar>
        {/* Hamburger menu button - only visible on mobile */}
        <IconButton
          color="inherit"
          aria-label="toggle sidebar"
          aria-expanded={sidebarOpen}
          onClick={onMenuClick}
          edge="start"
          sx={{
            mr: 2,
            color: "#7357FF",
            display: { xs: "flex", md: "none" },
          }}
        >
          <MenuIcon />
        </IconButton>

        {/* Page title */}
        <Typography
          variant="h6"
          noWrap
          component="div"
          sx={{
            flexGrow: 1,
            fontWeight: 500,
            color: "#333",
          }}
        >
          {title}
        </Typography>

        {/* User avatar menu - only show on mobile since sidebar has sign out on desktop */}
        {isMobile && (
          <Box sx={{ flexGrow: 0 }}>
            <Tooltip title="Account settings">
              <IconButton onClick={handleOpenUserMenu} sx={{ p: 0 }}>
                <Avatar
                  alt="User"
                  sx={{
                    width: 36,
                    height: 36,
                    backgroundColor: "#7357FF",
                  }}
                />
              </IconButton>
            </Tooltip>
            <Menu
              sx={{ mt: "45px" }}
              id="menu-appbar"
              anchorEl={anchorElUser}
              anchorOrigin={{
                vertical: "top",
                horizontal: "right",
              }}
              keepMounted
              transformOrigin={{
                vertical: "top",
                horizontal: "right",
              }}
              open={Boolean(anchorElUser)}
              onClose={handleCloseUserMenu}
            >
              <MenuItem
                onClick={() => {
                  signOut();
                  handleCloseUserMenu();
                }}
              >
                <Typography textAlign="center">Sign Out</Typography>
              </MenuItem>
            </Menu>
          </Box>
        )}
      </Toolbar>
    </AppBar>
  );
};
